import { useCallback, useEffect, useRef, useState } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { getMediaClient } from '@atlaskit/media-client-react/get-media-client';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import {
	buildImageUrl,
	IMAGE_CREATE_KEY,
	isImageCreateData,
} from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import { fetchMauiPersonalReadToken } from '@atlassian/conversation-assistant-service/common/utils/maui-media-token';
import {
	type AssistanceServiceInjectedParams,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { isStaging } from '@atlassian/conversation-assistant-viz/utils/env-helpers';

import type {
	AIHeaderImageStatus,
	GenerateImageProps,
	UseRemixImageGenerationProps,
	UseRemixImageGenerationReturn,
} from '../types';

const PRODUCT = 'confluence';

type RemixStreamMessage = {
	message?: {
		content?: string;
		message?: {
			actions?: Array<{ data?: unknown; key?: string }>;
		};
	};
	type?: string;
};

const unwrapStreamMessage = (value: unknown): RemixStreamMessage => {
	if (
		typeof value === 'object' &&
		value !== null &&
		'__isSSEWrapper' in value &&
		value.__isSSEWrapper === true &&
		'data' in value
	) {
		return value.data as RemixStreamMessage;
	}

	return value as RemixStreamMessage;
};

const getGeneratedMediaFileId = async (generator: AsyncIterable<unknown>): Promise<string> => {
	for await (const value of generator) {
		const streamMessage = unwrapStreamMessage(value);

		if (streamMessage.type === 'ERROR') {
			throw new Error(streamMessage.message?.content ?? 'Remix image generation failed');
		}

		if (streamMessage.type === 'FINAL_RESPONSE') {
			const imageAction = streamMessage.message?.message?.actions?.find(
				(action) => action.key === IMAGE_CREATE_KEY,
			);
			if (!isImageCreateData(imageAction?.data)) {
				throw new Error('Remix did not return a generated image');
			}

			return imageAction.data.mediaFileId;
		}
	}

	throw new Error('Remix image generation ended without a response');
};

const useRemixImageGenerationWithAssistanceService = ({
	contentId,
	createEditorMediaProvider,
	userId,
	assistanceServiceInjectedParams: { assistanceService },
}: UseRemixImageGenerationProps &
	AssistanceServiceInjectedParams): UseRemixImageGenerationReturn => {
	const [aiHeaderImageStatus, setAIHeaderImageStatus] =
		useState<AIHeaderImageStatus>('NOT_LOADING');
	const [aiHeaderImageFileId, setAIHeaderImageFileId] = useState<string>();
	const [error, setError] = useState<string | null>(null);
	const activeAbortControllerRef = useRef<AbortController>();
	const generationSequenceRef = useRef(0);

	useEffect(
		() => () => {
			activeAbortControllerRef.current?.abort();
		},
		[],
	);

	const uploadGeneratedImage = useCallback(
		async (mediaFileId: string): Promise<string> => {
			if (!userId) {
				throw new Error('Cannot upload a Remix image without a user ID');
			}
			if (!createEditorMediaProvider) {
				throw new Error('Cannot upload a Remix image without an editor media provider');
			}

			const mediaProvider = await createEditorMediaProvider({
				contentId,
				contentType: 'page',
				isExternalShare: false,
			});
			const targetCollection = mediaProvider.uploadParams?.collection;
			if (!mediaProvider.uploadMediaClientConfig || !targetCollection) {
				throw new Error('Editor media provider is missing upload configuration');
			}

			const { token, collection } = await fetchMauiPersonalReadToken(PRODUCT);
			const sourceUrl = buildImageUrl({
				fileId: mediaFileId,
				token: token.split('Bearer ')[1],
				collection,
				clientId: userId,
				isStaging: isStaging(),
			});
			const mediaClient = getMediaClient(mediaProvider.uploadMediaClientConfig);
			const result = await mediaClient.file.uploadExternal(
				sourceUrl,
				targetCollection,
				undefined,
				true,
			);

			return result.uploadableFileUpfrontIds.id;
		},
		[contentId, createEditorMediaProvider, userId],
	);

	const generateImage = useCallback(
		async ({
			adfContent,
			aspectRatio,
			showAcceptanceFlow = false,
			theme,
		}: GenerateImageProps): Promise<string | undefined> => {
			activeAbortControllerRef.current?.abort();

			const generationSequence = ++generationSequenceRef.current;
			const conversationId = crypto.randomUUID();
			const abortController = new AbortController();
			activeAbortControllerRef.current = abortController;
			setAIHeaderImageFileId(undefined);
			setError(null);
			setAIHeaderImageStatus(showAcceptanceFlow ? 'LOADING_WITH_ACCEPTANCE_FLOW' : 'LOADING');

			const prompt: DocNode = {
				type: 'doc',
				version: 1,
				content: [
					{
						type: 'paragraph',
						content: [{ type: 'text', text: adfContent }],
					},
				],
			};
			const dynamicUiSource = {
				contentType: 'page',
				startLocalId: '',
				endLocalId: '',
				startIndex: 0,
				endIndex: -1,
				fragmentAdf: prompt.content,
				fullAdf: prompt,
			};

			try {
				const creationContext = {
					dynamicUiType: 'image',
					dynamicUiSubtype: theme ?? 'image',
					dynamicUiSource,
					source: 'remix',
					isStagingArea: true,
					productKey: PRODUCT,
					isViewMode: false,
					...(expValEquals('rovo_remix_experience_context', 'isEnabled', true)
						? { experience: 'remix', blocks: ['image'] }
						: {}),
					...(aspectRatio ? { additionalContext: { aspectRatio } } : {}),
				};
				const { generator } = await assistanceService.sendMessageStream({
					message: prompt,
					conversationId,
					controller: abortController,
					storeMessage: false,
					citationsEnabled: true,
					optInSSEStream: true,
					additionalContext: {
						creationContext,
						userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
					},
				});
				const temporaryMediaFileId = await getGeneratedMediaFileId(generator);
				const generatedImageId = await uploadGeneratedImage(temporaryMediaFileId);

				if (generationSequence === generationSequenceRef.current) {
					setAIHeaderImageFileId(generatedImageId);
					setAIHeaderImageStatus('SUCCESSFULLY_SET');
				}
				return generatedImageId;
			} catch (generationError) {
				if (generationSequence === generationSequenceRef.current) {
					const message =
						generationError instanceof Error ? generationError.message : 'Unknown error occurred';
					setError(message);
					setAIHeaderImageStatus('ERROR');
				}
				return undefined;
			} finally {
				if (activeAbortControllerRef.current === abortController) {
					activeAbortControllerRef.current = undefined;
				}
			}
		},
		[assistanceService, uploadGeneratedImage],
	);

	return {
		generateImage,
		loading:
			aiHeaderImageStatus === 'LOADING' || aiHeaderImageStatus === 'LOADING_WITH_ACCEPTANCE_FLOW',
		error,
		aiHeaderImageStatus,
		aiHeaderImageFileId,
	};
};

const useRemixImageGenerationWithService = createHookWithAssistanceService(
	useRemixImageGenerationWithAssistanceService,
);

export const useRemixImageGeneration = (
	props: UseRemixImageGenerationProps,
): UseRemixImageGenerationReturn =>
	useRemixImageGenerationWithService({
		...props,
		assistanceServiceParams: {
			product: PRODUCT,
			experienceId: 'ai-mate',
			siteId: props.cloudId,
			touchpointSource: 'company-hub-image-generation',
		},
	});
